import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import styles from "./Connect.module.css";
import Api from "../../utils/Api/Api";
import { track as trackEvent } from "../../utils/analyticsService";
import { useAuthGate, hasSession } from "../../components/AuthModal/AuthGate";
import { endSession } from "../../utils/session";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";

/*
 * web.getfleek.app/connect: the sign-in step of connecting an AI assistant
 * (fleek-backend assistants/CONTRACT.md).
 *
 * The assistant's connector (account-mcp.getfleek.app) has already shown its
 * own consent screen and sends the browser here with a single-use `state`.
 * The user signs in with the normal sheet, sees which account is connecting,
 * and taps Allow: the backend mints a one-time code and the browser goes back
 * to the connector's /callback with it. The callback address is fixed here,
 * never read from the URL, so this page cannot be turned into a redirect to
 * anywhere else. `assistant` and `host` are for display only.
 */
export const CALLBACK_URL =
  process.env.REACT_APP_ASSISTANT_CALLBACK_URL || "https://account-mcp.getfleek.app/callback";

const outcome = (res) => {
  const failed = res instanceof Error || res?.isAxiosError;
  const response = failed ? res.response : res;
  return {
    ok: !failed && !!response && response.status >= 200 && response.status < 300,
    status: response?.status || 0,
    data: response?.data || null,
  };
};

const track = (event, params = {}) => {
  try {
    trackEvent(event, params);
  } catch {
    /* analytics must never block connecting */
  }
};

const back = (params) => {
  const url = new URL(CALLBACK_URL);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  window.location.assign(url.toString());
};

const clip = (value, max) => String(value || "").trim().slice(0, max);

const READ = "subscriptions:read";
const ADD = "subscriptions:add";
// Cancel for me from the chat. The connector only offers it to assistants
// it has been switched on for, so most links never carry it.
const CANCEL = "subscriptions:cancel";
// What the connector's consent screen approved. Only known scopes pass, and
// read is always in; the backend refuses anything else and the connector
// issues nothing if what comes back differs from what it approved.
const scopesFrom = (raw) => {
  const asked = String(raw || "").split(/\s+/);
  return [READ, ...(asked.includes(ADD) ? [ADD] : []), ...(asked.includes(CANCEL) ? [CANCEL] : [])];
};

// The "can't" line, in today's words unless cancelling was approved.
const cantLine = (canAdd, canCancel) => {
  if (canCancel) return canAdd ? "It can't buy or remove anything." : "It can't buy, remove or change anything else.";
  return canAdd ? "It can't buy, cancel or remove anything." : "It can't buy, cancel or change anything.";
};

export const Connect = () => {
  const [params] = useSearchParams();
  const state = params.get("state") || "";
  const assistant = clip(params.get("assistant"), 40) || "Your assistant";
  const host = clip(params.get("host"), 255);
  const scopes = scopesFrom(params.get("scope"));
  const canAdd = scopes.includes(ADD);
  const canCancel = scopes.includes(CANCEL);
  const { requireAuth, isLoggedIn } = useAuthGate();

  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);

  useEffect(() => {
    const previous = document.title;
    document.title = `Connect ${assistant} | Fleek`;
    track("assistant_connect_viewed", { assistant, signed_in: hasSession() });
    return () => {
      document.title = previous;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whose account this is, so a shared browser does not connect the wrong one.
  useEffect(() => {
    if (!isLoggedIn) return;
    let live = true;
    Api.GetUserDetails()
      .then((res) => {
        const { ok, data } = outcome(res);
        const full = data?.name || data?.data?.name || "";
        if (live && ok) setName(String(full).trim().split(" ")[0]);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [isLoggedIn]);

  const approve = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      localStorage.setItem("returnToPage", `/connect?${params.toString()}`);
    } catch {
      /* storage blocked: the sheet flow does not need it */
    }
    const { ok, status, data } = outcome(
      await Api.ApproveAssistant({ assistant, assistant_host: host, scope: scopes.join(" ") }).catch((e) => e)
    );
    if (status === 401) return; // App's interceptor is sending the browser to /login.
    try {
      localStorage.removeItem("returnToPage");
    } catch {
      /* ignore */
    }
    if (ok && data?.code) {
      track("assistant_connect_approved", { assistant, can_add: canAdd, ...(canCancel ? { can_cancel: true } : {}) });
      back({ state, code: data.code });
      return;
    }
    inFlight.current = false;
    setBusy(false);
    setError("That didn't go through. Please try again.");
  }, [assistant, host, params, state, scopes, canAdd, canCancel]);

  const decline = () => {
    track("assistant_connect_declined", { assistant });
    back({ state, error: "access_denied" });
  };

  const switchAccount = () => {
    endSession();
    window.location.reload();
  };

  if (!state) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <img className={styles.wordmark} src={FleekIcon} alt="Fleek" />
          <div className={styles.card}>
            <h1 className={styles.title}>This link is incomplete</h1>
            <p className={styles.body}>Go back to your assistant and connect Fleek again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <img className={styles.wordmark} src={FleekIcon} alt="Fleek" />
        <div className={styles.card}>
          <h1 className={styles.title}>Connect {assistant} to Fleek</h1>
          {host && <p className={styles.muted}>Access goes to {host}.</p>}
          <ul className={styles.list}>
            <li>It can see the subscriptions you track in Fleek: names, prices, renewal dates and your monthly total.</li>
            {canAdd && <li>It can add subscriptions you tell it about.</li>}
            {canCancel && (
              <li>
                It can ask Fleek to cancel a subscription for you, one at a time and only after you say yes in the chat. If
                the brand needs you to sign in, you do it yourself on its own page in a secure browser Fleek opens for you,
                never in the chat.
              </li>
            )}
            <li>{cantLine(canAdd, canCancel)}</li>
            <li>It never sees your payment details, codes or phone number.</li>
          </ul>
          {isLoggedIn ? (
            <>
              <p className={styles.account}>
                {name ? `Signed in as ${name}.` : "Signed in."}{" "}
                <button type="button" className={styles.link} onClick={switchAccount}>
                  Not you?
                </button>
              </p>
              {error && <div className={styles.error} role="alert">{error}</div>}
              <button type="button" className={styles.primary} onClick={approve} disabled={busy}>
                {busy ? "Connecting…" : "Allow"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.primary}
              onClick={() =>
                // Every sign-in the apps offer, whatever store this browser is in:
                // the account being connected may have been made any of those ways.
                requireAuth(
                  approve,
                  `Sign in to connect ${assistant} to Fleek, the same way you sign in to the Fleek app. New to Fleek? This creates your account.`,
                  { allMethods: true }
                )
              }
            >
              Sign in or create an account
            </button>
          )}
          <button type="button" className={styles.secondary} onClick={decline} disabled={busy}>
            Cancel
          </button>
          <p className={styles.muted}>You can disconnect any time from your assistant's connector settings.</p>
        </div>
      </div>
    </div>
  );
};

export default Connect;
